<template>
  <div class="min-w-[280px] overflow-auto h-full">
    <div class="flex flex-wrap items-center justify-between my-4 lg:my-8 pr-4">
      <!-- title -->
      <div class="flex-grow flex items-center">
        <svg
          viewBox="0 0 40 40"
          class="block w-8 h-8 stroke-current text-navy"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)">
            <path
              d="M10.719,5.2A5.251,5.251,0,0,0,.75,7.5V23.25h3"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M0.75 12.75L3.75 12.75"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M0.75 8.25L9 8.25"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M21.75 15L21.75 23.25 6.75 23.25 6.75 15"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M5.25,16.5l7.939-7.939a1.5,1.5,0,0,1,2.122,0L23.25,16.5"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M14.25,12.375a.375.375,0,0,1,.375.375"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M13.875,12.75a.375.375,0,0,1,.375-.375"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M14.25,13.125a.375.375,0,0,1-.375-.375"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M14.625,12.75a.375.375,0,0,1-.375.375"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
            <path
              d="M17.25,17.25h-6a1.5,1.5,0,0,0-1.5,1.5v4.5h9v-4.5A1.5,1.5,0,0,0,17.25,17.25Z"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
            ></path>
          </g>
        </svg>

        <span class="ml-2 text-navy font-bold leading-7 text-2xl">
          <template v-if="$page.props.auth.user && $page.props.auth.user.role === 'farm_owner'">My Farm</template>
          <template v-else>Farmers</template>
        </span>
      </div>

      <!-- Back to Farmers -->
      <div class="">
        <inertia-link
          @click="goBack"
          class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          :href="route('app.farmer.index')"
        >
          <svg
            viewBox="0 0 40 40"
            xmlns="http://www.w3.org/2000/svg"
            class="block w-7 h-7 stroke-current text-white"
          >
            <g
              transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)"
            >
              <path
                d="M7.501 11.997L16.501 11.997"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              ></path>
              <path
                d="M11.251 15.747L7.501 11.997 11.251 8.247"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              ></path>
            </g>
          </svg>
          <span class="sr-only"> Farmers List </span>
        </inertia-link>
      </div>
    </div>

    <flash-message />

    <!-- Consolidated Farmer Header -->
    <div class="bg-white w-full border-b border-navy-light">
      <div class="flex items-start p-4 gap-6">
        <div class="min-w-0 flex-shrink-0" style="width: 280px;">
          <h3 class="text-navy font-bold text-xl cursor-pointer" @click="toggleHeader">{{ farmer.name }}</h3>
          <div v-show="headerCollapsed" class="text-[13px] text-gray-600 mt-1">
            {{ compactAddress }}
          </div>
          <div v-show="!headerCollapsed" class="text-[13px] leading-5 text-gray-700 whitespace-pre-line mt-2">
            <template v-if="farmer.address_line_1">{{ farmer.address_line_1 }}<br /></template>
            <template v-if="farmer.address_line_2">{{ farmer.address_line_2 }}<br /></template>
            <template v-if="farmer.city">{{ farmer.city }}<span v-if="farmer.county">, {{ farmer.county }}</span><br /></template>
            <template v-if="!farmer.city && farmer.county">{{ farmer.county }}<br /></template>
            <template v-if="farmer.eircode">{{ farmer.eircode }}</template>
          </div>
        </div>
        <div v-show="!headerCollapsed" class="flex flex-col text-[13px] gap-1 min-w-0 flex-shrink-0" style="width: 220px;">
          <div><strong>Contact:</strong> {{ contactName || 'Not Set' }}</div>
          <div><strong>Email:</strong> {{ farmer.email || 'Not Set' }}</div>
          <div><strong>Phone:</strong> {{ farmer.mobile || 'Not Set' }}</div>
          <div v-if="$page.props.auth.user.role == 'admin'"><strong>Organisation:</strong> {{ farmer.organisation || 'Not Set' }}</div>
          <div><strong>Assigned Consultant:</strong> {{ farmer.consultant_user ? farmer.consultant_user.first_name + ' ' + farmer.consultant_user.last_name : 'Not Set' }}</div>
          <div><strong>Herd No:</strong> {{ farmer.herd_no || 'Not Set' }}</div>
          <div><strong>Account #:</strong> {{ farmer.account_number || 'Not Set' }}</div>
        </div>
        <div v-show="!headerCollapsed" class="flex flex-col text-[13px] gap-1 min-w-0 flex-shrink-0" style="width: 200px;">
          <div class="flex flex-col gap-1">
            <span class="px-2 py-1 rounded text-xs font-medium w-fit" :class="farmer.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">Active: {{ farmer.enabled ? 'Yes' : 'No' }}</span>
            <span class="px-2 py-1 rounded text-xs font-medium w-fit" :class="farmer.farm_type ? 'bg-purple-100 text-purple-800' : 'bg-yellow-100 text-yellow-800'">Farm Type: {{ farmer.farm_type ? getFarmTypeLabel(farmer.farm_type) : 'Not Set' }}</span>
            <span
              class="px-2 py-1 rounded text-xs font-medium w-fit"
              :class="
                farmer.derogation === 1
                  ? 'bg-blue-100 text-blue-800'
                  : (farmer.derogation === 0
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-yellow-100 text-yellow-800')
              "
            >
              Derogation:
              {{
                farmer.derogation === 1
                  ? 'Yes'
                  : (farmer.derogation === 0
                      ? 'No'
                      : 'Not Set')
              }}
            </span>
            <span class="px-2 py-1 rounded text-xs font-medium w-fit" :class="farmer.organic === 1 ? 'bg-green-100 text-green-800' : (farmer.organic === 0 ? 'bg-gray-100 text-gray-700' : 'bg-yellow-100 text-yellow-800')">Organic: {{ farmer.organic === 1 ? 'Yes' : (farmer.organic === 0 ? 'No' : 'Not Set') }}</span>
            <span class="px-2 py-1 rounded text-xs font-medium w-fit" :class="
              farmer.coop_program === 1 || farmer.coop_program === true
                ? 'bg-green-100 text-green-800'
                : (farmer.coop_program === 0 || farmer.coop_program === false
                    ? 'bg-gray-100 text-gray-700'
                    : 'bg-yellow-100 text-yellow-800')
            ">
              Co-op Programme: {{
                farmer.coop_program === 1 || farmer.coop_program === true
                  ? 'Yes'
                  : (farmer.coop_program === 0 || farmer.coop_program === false
                      ? 'No'
                      : 'Not Set')
              }}
            </span>
          </div>
        </div>
        <div class="flex items-start gap-2 ml-auto">
          <div class="relative" @keydown.escape="showActions=false">
            <button @click.stop="toggleActions" class="w-9 h-9 mt-0 flex items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" :aria-expanded="showActions.toString()" aria-haspopup="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-gray-600"><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></svg>
              <span class="sr-only">Farmer actions</span>
            </button>
            <transition name="fade">
              <div v-if="showActions" class="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-20 py-1 text-sm">
                <inertia-link :href="route('app.farmer.edit', { farmer: farmer.id, return: 'view' })" class="block px-3 py-2 hover:bg-gray-50">Edit Farmer</inertia-link>
              </div>
            </transition>
          </div>
          <button @click.stop="toggleHeader" class="cursor-pointer">
            <svg 
              class="w-5 h-5 text-navy transition-transform duration-200" 
              :class="{ 'rotate-180': !headerCollapsed }"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="bg-gradient-to-b from-gray-50 to-white">
    <nav class="flex w-full overflow-auto px-2 pt-2" aria-label="Tabs">
      <!-- Farms -->
        <a
          v-on:click.stop="tab = 'farms'"
          :class="tabClass('farms')"
          class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-t-md border-b-2 cursor-pointer transition-colors"
        >
          <svg
            :class="{
              'text-navy-dark': tab == 'parcel',
              'stroke-2': tab == 'parcel',
            }"
            class="text-navy group-hover:text-navy-dark stroke-current stroke-1 w-4 h-4 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 40 40"
          >
            <g
              transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)"
            >
              <path
                d="M10.719,5.2A5.251,5.251,0,0,0,.75,7.5V23.25h3"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M0.75 12.75L3.75 12.75"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M0.75 8.25L9 8.25"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M21.75 15L21.75 23.25 6.75 23.25 6.75 15"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M5.25,16.5l7.939-7.939a1.5,1.5,0,0,1,2.122,0L23.25,16.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M14.25,12.375a.375.375,0,0,1,.375.375"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M13.875,12.75a.375.375,0,0,1,.375-.375"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M14.25,13.125a.375.375,0,0,1-.375-.375"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M14.625,12.75a.375.375,0,0,1-.375.375"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M17.25,17.25h-6a1.5,1.5,0,0,0-1.5,1.5v4.5h9v-4.5A1.5,1.5,0,0,0,17.25,17.25Z"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </g>
          </svg>
          <span>All Farms</span>
        </a>

        <!-- Tasks -->
        <a
          v-on:click.stop="tab = 'tasks'"
          :class="tabClass('tasks')"
          class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-t-md border-b-2 cursor-pointer transition-colors"
        >
          <svg
            :class="{
              'text-navy-dark': tab == 'tasks',
              'stroke-2': tab == 'tasks',
            }"
            class="text-navy group-hover:text-navy-dark stroke-current stroke-1 w-4 h-4 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 40 40"
          >
            <g
              transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)"
            >
              <path
                d="M13.045,18.636l-3.712.53.53-3.712,9.546-9.546A2.25,2.25,0,0,1,22.591,9.09Z"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M5.250 0.749 L12.750 0.749 L12.750 3.749 L5.250 3.749 Z"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M12.75,2.249h3a1.5,1.5,0,0,1,1.5,1.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M17.25,18.749v3a1.5,1.5,0,0,1-1.5,1.5H2.25a1.5,1.5,0,0,1-1.5-1.5v-18a1.5,1.5,0,0,1,1.5-1.5h3"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M5.25 8.249L12.75 8.249"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M5.25 12.749L8.25 12.749"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </g>
          </svg>
          <span>Tasks</span>
        </a>

        <!-- Reports -->
        <a
          v-if="false"
          v-on:click.stop="tab = 'reports'"
          :class="tabClass('reports')"
          class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-t-md border-b-2 cursor-pointer transition-colors"
        >
          <svg
            :class="{
              'text-navy-dark': tab == 'reports',
              'stroke-2': tab == 'reports',
            }"
            class="text-navy group-hover:text-navy-dark stroke-current stroke-1 w-4 h-4 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 40 40"
          >
            <g
              transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)"
            >
              <path
                d="M8.492 11.265L7.432 15.508 11.674 14.447 18.038 8.083 14.856 4.901 8.492 11.265z"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M21.751,4.371l1.06,1.06a1.5,1.5,0,0,1,0,2.122L19.5,10.863"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M18.038,8.083l3.713-3.712a1.5,1.5,0,0,0,0-2.122L20.69,1.189a1.5,1.5,0,0,0-2.121,0L14.856,4.9"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M20.251,13.5v3.128a1.5,1.5,0,0,1-.44,1.061L14.69,22.81a1.5,1.5,0,0,1-1.061.44H2.25a1.5,1.5,0,0,1-1.5-1.5V2.25A1.5,1.5,0,0,1,2.25.75H13.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M14.251,23.115V18.75a1.5,1.5,0,0,1,1.5-1.5h4.365"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </g>
          </svg>
          <span>Reports</span>
        </a>

        <!-- Users -->
        <a
          v-on:click.stop="tab = 'users'"
          :class="tabClass('users')"
          class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-t-md border-b-2 cursor-pointer transition-colors"
        >
          <svg
            :class="{
              'text-navy-dark': tab == 'users',
              'stroke-2': tab == 'users',
            }"
            class="text-navy group-hover:text-navy-dark stroke-current stroke-1 w-4 h-4 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 40 40"
          >
            <g
              transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)"
            >
              <path
                d="M2.250 6.000 A2.250 2.250 0 1 0 6.750 6.000 A2.250 2.250 0 1 0 2.250 6.000 Z"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M4.5,9.75A3.75,3.75,0,0,0,.75,13.5v2.25h1.5l.75,6H6"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M17.250 6.000 A2.250 2.250 0 1 0 21.750 6.000 A2.250 2.250 0 1 0 17.250 6.000 Z"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M19.5,9.75a3.75,3.75,0,0,1,3.75,3.75v2.25h-1.5l-.75,6H18"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M9.000 3.750 A3.000 3.000 0 1 0 15.000 3.750 A3.000 3.000 0 1 0 9.000 3.750 Z"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M17.25,13.5a5.25,5.25,0,0,0-10.5,0v2.25H9l.75,7.5h4.5l.75-7.5h2.25Z"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </g>
          </svg>
          <span>Users</span>
        </a>
      </nav>
      <div class="h-[1px] w-full bg-gray-200"></div>
    </div>

    <div v-show="tab == 'farms'" class="w-full">
      <farmer-farms
        :zones="zones"
        :farms="farms"
        :farmer="farmer"
        :showSamplePath="showSamplePath"
      />
    </div>

    <div v-show="tab == 'tasks'" class="w-full">
      <farmer-tasks :farmer="farmer" :tasks="tasks" />
    </div>

    <div v-if="false" v-show="tab == 'reports'" class="w-full">
      <farmer-reports />
    </div>

    <div v-show="tab == 'users'" class="w-full">
      <farmer-users :users="users" :farmer="farmer" />
    </div>
  </div>
</template>

<script>
import { showDafmLpis } from "@/Partials/Map/Util/DafmLpisPage";
import AppLayout from "@/Layouts/App";
import FarmerFarms from "@/Pages/Farmer/Partials/Farms";
import FarmerTasks from "@/Pages/Farmer/Partials/Tasks";
import FarmerReports from "@/Pages/Farmer/Partials/Reports";
import FarmerUsers from "@/Pages/Farmer/Partials/Users";

export default {
  layout: [AppLayout],
  components: {
    FarmerFarms,
    FarmerTasks,
    FarmerReports,
    FarmerUsers,
  },

  props: {
    farmer: Object,
    zones: Array,
    users: Array,
    farms: Object,
    tasks: Array,
    tabView: String,
    showPaths: String,
    organisationId: String,
  },

  data() {
    return {
  tab: "farms",
  showActions: false,
      showSamplePath: null,
      headerCollapsed: true,
    };
  },

  mounted() {
    if (this.tabView && this.tabView !== 'info') {
      this.tab = this.tabView;
    }
    this.showSamplePath = this.showPaths || null;
    this.loadMapForFarmer();

    //check for organisation with name of ISEC
    if (this.farmer.organisation == "ISEC") {
      //load LPIS Map Details
      this.$root.Lpis.source = "isec";
      this.$root.Lpis.herdNo = this.farmer.herd_no;
      this.$root.Lpis.show = true;
      this.$root.Lpis.show_zone = false;
      this.$root.Lpis.showLpisAreaDetails = this.farmer.farmer_farms_collected;
      this.$root.Lpis.filterCompletedHerdNo = true;

      this.$root.services.dropoff_points = true;
      this.$root.services.sampler_location = true;
    } else {
      // The farmer's linked parcels come with the map data
      showDafmLpis(this.$root.Lpis);
    }
  },

  methods: {
    getFarmTypeLabel(farmType) {
      const farmTypes = {
        'dairy': 'Dairy',
        'cattle': 'Cattle Rearing',
        'cattle_other': 'Cattle Other',
        'sheep': 'Sheep',
        'tillage': 'Tillage',
        'mixed_livestock': 'Mixed Livestock'
      };
      return farmTypes[farmType] || farmType;
    },
    tabClass(name) {
      const isActive = this.tab === name;
      return [
        'border-b-2',
        isActive
          ? 'bg-white text-navy-dark border-navy-dark shadow-sm'
          : 'text-navy border-transparent hover:text-navy-dark hover:border-navy-dark',
      ].join(' ');
    },
    goBack() {
      try {
        this.$emitter.emit("cancelFarmInteraction");
        // this.loadMapForFarmer();
      } catch (e) {}
    },

    loadMapForFarmer() {
      let vm = this;

      axios
        .post(route("app.farmer.map.view", this.farmer.id), {
          view: "parcels",
        })
        .then(function (response) {
          // Check if component is still mounted before setting farmer data
          if (vm._isDestroying || !vm.$el) {
            return;
          }
          
          vm.$root.farmer = response.data;
          vm.$root.showSearch = false;
        })
        .catch(function (error) {
          console.error('Farmer View: Error loading farmer map data:', error);
        });
    },
    toggleActions() {
      this.showActions = !this.showActions;
    },
    toggleHeader() {
      this.headerCollapsed = !this.headerCollapsed;
    }
  },
  beforeDestroy() {
    // Mark component as being destroyed to prevent async operations from completing
    this._isDestroying = true;
    
    // Clear farmer data to trigger map cleanup when exiting farmer view
    this.$root.farmer = null;
  },
  computed: {
    contactName() {
      // Prefer explicit farmer.contact_name, fallback to consultant_user name, else null
      if (this.farmer && this.farmer.contact_name) {
        return this.farmer.contact_name;
      }
      if (this.farmer && this.farmer.consultant_user) {
        return this.farmer.consultant_user.first_name + ' ' + this.farmer.consultant_user.last_name;
      }
      return null;
    },
    compactAddress() {
      const parts = [];
      if (this.farmer.address_line_1) parts.push(this.farmer.address_line_1);
      if (this.farmer.city) parts.push(this.farmer.city);
      if (this.farmer.county && this.farmer.city !== this.farmer.county) parts.push(this.farmer.county);
      if (this.farmer.eircode) parts.push(this.farmer.eircode);
      return parts.join(', ') || 'No address set';
    }
  }
};
</script>
